import { Component, ViewChild } from '@angular/core';
import { MppService } from '@app/mpp/service/mpp.service';
import { PopoverController, ViewWillEnter } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PanAppState, PlatformService } from '@panamax/app-state';
import { firstValueFrom, Observable } from 'rxjs';
import { recipeDetailViewModel } from '@app/mpp/model/recipe-detail-view-model';
import { RecipeDetailService } from './services/recipe-detail.service';
import { Crumb } from '@shared/components/breadcrumbs/breadcrumbs.component';
import { PATHS } from '@shared/constants/paths';
import {
  IngredientSearchResult,
  LastVisitedType,
  RecipeDetailTabs,
} from '@usf/ngrx-tools';
import {
  cleanServingPerRecipeValue,
  cleanServingValue,
  cleanYieldValue,
} from './helper/recipe-detail-helper';
import { IngredientCardClickEvent } from '@app/mpp/model/ingredient-card-click-event';

@Component({
  selector: 'app-recipe-detail',
  templateUrl: './recipe-detail.page.html',
  styleUrls: ['./recipe-detail.page.scss'],
})
export class RecipeDetailPage implements ViewWillEnter {
  viewModel$: Observable<recipeDetailViewModel>;
  imageSrc$: Observable<string>;
  breadcrumbs: Crumb[];
  tabRef = RecipeDetailTabs;
  isEditingNameDescription = false;
  isModalOpen$ = this.recipeDetailService.groupModalIsOpen$;
  isEditDetailsModalOpen$ = this.recipeDetailService.editDetailsModalIsOpen$;
  isIngredientSearchModalOpen$ =
    this.recipeDetailService.ingredientSearchModalIsOpen$;
  isProfileImageModalOpen$ = this.recipeDetailService.profileImageModalIsOpen$;
  nameContainsEmojis = false;
  nameIsDuplicate = false;
  nameIsEmpty = false;
  descriptionContainsEmojis = false;
  searchResultsForIngredient$ =
    this.recipeDetailService.searchResultsForIngredient;
  searchResultsForProduct$ = this.recipeDetailService.typeAheadResults;
  searchTimeoutTracker$ = this.recipeDetailService.searchTimeoutTracker$;
  searchNavTimeout;
  constructor(
    public mppService: MppService,
    readonly platformService: PlatformService,
    private panAppState: PanAppState,
    readonly translateService: TranslateService,
    public recipeDetailService: RecipeDetailService,
    private popoverController: PopoverController,
  ) {
    this.viewModel$ = this.recipeDetailService.recipeViewModel$;
  }

  ionViewWillEnter(): void {
    this.setupBreadCrumbsAndTitle();
    this.recipeDetailService.updateShowHidePortion(false);
    this.isEditingNameDescription = false;
    this.nameContainsEmojis = false;
    this.descriptionContainsEmojis = false;
    this.recipeDetailService.handleGroupNameModalDismiss();
    this.recipeDetailService.dismissEditDetailsModal();
    this.recipeDetailService.dismissProfileImageModal();
  }

  async setupBreadCrumbsAndTitle() {
    const recipe = await firstValueFrom(this.viewModel$);
    const name =
      recipe.recipeName.substring(0, 25) +
      (recipe.recipeName.length > 25 ? '...' : '');
    this.breadcrumbs = [
      {
        label: 'i18n.breadcrumbs.mpp',
        url: '/' + PATHS.MPP,
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.mppRecipes',
        url: '/' + PATHS.MPP + '/' + PATHS.RECIPES,
        active: false,
      },
      {
        label: name,
        url: '',
        active: true,
      },
    ];
    this.panAppState.setPageTitle(name);
    this.mppService.lastVisitedPage({
      lastVisited: LastVisitedType.RECIPE,
      id: recipe.recipeId,
    });
  }

  closePopover = async () => {
    if (this.popoverController.getTop()) {
      await this.popoverController.dismiss();
    }
  };

  goToIngredients(recipeId: string) {
    this.mppService.goToIngredients(recipeId);
  }

  shouldCloselockInfoPopover(event: any) {
    window.addEventListener('blur', this.closePopover, { once: true });
  }

  removeWindowListener = () => {
    window.removeEventListener('blur', this.closePopover);
  };

  clickShowHidePortion = (currentValue: boolean) => {
    this.recipeDetailService.updateShowHidePortion(!currentValue);
  };

  handleYieldChange(value: number, recipeID: string) {
    this.recipeDetailService.updateYield(value, recipeID);
  }

  handleYieldInputChange(event: any, recipeID: string) {
    const cleanedValue = cleanYieldValue(event?.target.value);
    this.recipeDetailService.updateYield(cleanedValue, recipeID);
  }

  handleServingInputChange(event: any, recipeID: string) {
    const cleanedValue = cleanServingValue(event?.target.value);
    this.recipeDetailService.updateServing(cleanedValue, recipeID);
  }

  handleServingPerRecipeInputChange(event: any, recipeID: string) {
    const cleanedValue = cleanServingPerRecipeValue(event?.target.value);
    this.recipeDetailService.updateServingPerRecipe(cleanedValue, recipeID);
  }

  handleServingChange(value: number, recipeID: string) {
    this.recipeDetailService.updateServing(value, recipeID);
  }

  handleServingPerRecipeChange(value: number, recipeID: string) {
    this.recipeDetailService.updateServingPerRecipe(value, recipeID);
  }

  handleShowServingUOM(event: Event, recipeID: string) {
    this.recipeDetailService.showServingUOMDropdown(event, recipeID);
  }

  handleShowYieldUOM(event: Event, recipeID: string) {
    this.recipeDetailService.showYieldUOMDropdown(event, recipeID);
  }

  toggleEditNameDescription() {
    this.isEditingNameDescription = !this.isEditingNameDescription;
  }

  handleShowGroup(event: Event, recipeID: string) {
    this.recipeDetailService.showGroupNameDropdown(event, recipeID);
  }

  async onNameChange(value: string) {
    this.nameContainsEmojis = /\p{Extended_Pictographic}/u.test(value);
    this.nameIsDuplicate = await this.mppService.doesMppNameExists(
      value,
      'recipe',
    );
    this.nameIsEmpty = !value || value.trim() === '';
  }

  onDescriptionChange(value: string) {
    this.descriptionContainsEmojis = /\p{Extended_Pictographic}/u.test(value);
  }

  handleRecipeNameChange(event: any, recipeID: string) {
    if (
      !this.nameContainsEmojis &&
      !this.nameIsDuplicate &&
      !this.nameIsEmpty
    ) {
      const cleanedValue = event.target.value?.trim();
      this.recipeDetailService.updateRecipeName(cleanedValue, recipeID);
    }
  }

  handleDescriptionChange(event: any, recipeID: string) {
    if (!this.descriptionContainsEmojis) {
      const cleanedValue = event.target.value?.trim();
      this.recipeDetailService.updateDescription(cleanedValue, recipeID);
    }
  }

  handleGroupNameModalDismiss() {
    this.recipeDetailService.handleGroupNameModalDismiss();
  }

  handleCustomGroupSubmit(groupName: string, recipeId: string) {
    this.recipeDetailService.handleCustomGroupSubmit(groupName, recipeId);
  }

  openEditDetailsModal(): void {
    this.recipeDetailService.openEditDetailsModal();
  }

  dismissEditDetailsModal(): void {
    this.recipeDetailService.dismissEditDetailsModal();
  }

  handleEditDetailsSubmission(
    updates: Map<string, string>,
    recipeId: string,
  ): void {
    this.recipeDetailService.handleEditDetailsSubmission(updates, recipeId);
  }

  openProfileImageModal(): void {
    this.recipeDetailService.openProfileImageModal();
  }

  dismissProfileImageModal(): void {
    this.recipeDetailService.dismissProfileImageModal();
  }

  handleProfileImageOnSave(
    updates: Map<string, string>,
    recipeId: string,
  ): void {
    this.recipeDetailService.handleProfileImageModalOnSave(updates, recipeId);
  }

  async showMobileActions(recipeId: string): Promise<void> {
    await this.recipeDetailService.showMobileActions(recipeId);
  }

  async onNameClick(ingredientCard: IngredientCardClickEvent) {
    //will be using this function later for edit details
  }

  async openIngredientSearchModal(): Promise<void> {
    this.recipeDetailService.openIngredientSearchModal();
  }

  closeIngredientSearchModal(): void {
    this.recipeDetailService.closeIngredientSearchModal();
  }

  handleProductViewAll(searchKey: string): void {
    this.closeIngredientSearchModal();
    this.searchNavTimeout = setTimeout(() => {
      this.mppService.gotToProductSearch(searchKey);
    }, 50);
  }

  handleNewIngredientSearchRequest(value: string) {
    this.recipeDetailService.handleNewIngredientSearchRequest(value);
  }

  handleNewIngredientSearchSubmission(value: IngredientSearchResult) {
    this.recipeDetailService.addIngredient(value);
    this.closeIngredientSearchModal();
  }

  handleShowIngredientUom(event: IngredientCardClickEvent, recipeID: string) {
    const ingredientId = event.ingredientCard.ingredientId;
    const domEvent = event.event;
    this.recipeDetailService.showIngredientUOMDropdown(
      domEvent,
      recipeID,
      ingredientId,
    );
  }

  handleIngredientQuantityChange(
    event: IngredientCardClickEvent,
    recipeID: string,
  ) {
    const quantityElement = event.event.target as HTMLInputElement;
    const ingredientId = event.ingredientCard.ingredientId;
    const cleanedValue = quantityElement.value?.trim();
    this.recipeDetailService.updateIngredientQuantity(
      cleanedValue,
      recipeID,
      ingredientId,
    );
  }

  handleIngredientNoteChange(
    event: IngredientCardClickEvent,
    recipeID: string,
  ) {
    const ingredientId = event.ingredientCard.ingredientId;
    const noteElement = event.event.target as HTMLInputElement;
    const cleanedValue = noteElement.value?.trim();
    this.recipeDetailService.updateIngredientNote(
      cleanedValue,
      recipeID,
      ingredientId,
    );
  }
}
